import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import ProgressButton from "react-progress-button";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import Utils from "@utils";
import { AccountService } from "@services";
import { ENV_OPTIONS } from "@constants";
import { withMsal } from "@azure/msal-react";
import { ADOPERATIONS, ROUTES } from "@constants/index";
import BlueTextLogo from "../../images/rk-logo-blue.png";

import "./Login.scss";
import { SplashScreen } from "../../components/index";

class Login extends Component {
	constructor(props) {
		super(props);

		this.initialValidations = {
			email: false,
			password: false,
		};

		this.state = {
			email: "",
			password: "",
			buttonState: "",
			isProcessingMsal: false,
			validations: { ...this.initialValidations },
		};
	}

	updateField = ({ target: { name, value } }) => {
		this.setState({ [name]: value });
	};

	/** Validates the input fields required for a successful user login */
	validateLogin = () => {
		const { email } = this.state;
		const {
			flags: { federation: federationFlag },
		} = this.props;
		const newValidations = {};

		this.setState({ validations: this.initialValidations, buttonState: "loading" });

		newValidations.email = email === "";

		if (!federationFlag && Object.values(newValidations).includes(true)) {
			this.setState({ validations: newValidations });
			// The action button needs some time to animate the error
			setTimeout(() => {
				this.setState({ buttonState: "error" });
				clearTimeout();
			}, 400);
		} else {
			this.msalLoginRedirect();
		}
	};

	checkForEnter = event => {
		if (event.key === "Enter") {
			this.validateLogin();
		}
	};

	/** Performs the neccessary API calls to login the user */
	onLogin = () => {
		const { email, password } = this.state;
		const { lang } = global;
		const { onLogin } = this.props;

		AccountService.login({
			email,
			password,
		})
			.then(({ data }) => {
				this.setState({ buttonState: "success" });

				// Wait a little bit until progressButton display success state
				setTimeout(() => {
					onLogin(data);
					clearTimeout();
				}, 400);
			})
			.catch(error => {
				// tokens endpoint message || accounts endpoint message
				let message = error?.body?.error_description || error?.response?.data?.Message;

				if (!message) {
					message = lang.t("Invalid username or password");
				}
				this.setState({ buttonState: "error" });

				Utils.notify({
					type: "error",
					title: lang.t("Access Denied"),
					message,
				});
			});
	};

	msalLoginRedirect = () => {
		const { email } = this.state;
		const {
			msalContext: { instance: msalInstance },
		} = this.props;

		const {
			flags: { federation: federationFlag },
		} = this.props;

		if (federationFlag) {
			msalInstance.loginRedirect({
				authority: ENV_OPTIONS.adb2c.policies.signinV2,
				scopes: ENV_OPTIONS.adb2c.scopes,
				redirectUri: ROUTES.ADCallback,
				prompt: "login",
				state: Utils.encodeADState({ operation: ADOPERATIONS.login }),
			});
		} else {
			msalInstance.loginRedirect({
				loginHint: email,
				authority: ENV_OPTIONS.adb2c.policies.signin,
				scopes: ENV_OPTIONS.adb2c.scopes,
				redirectUri: ROUTES.ADCallback,
				prompt: "login",
				state: Utils.encodeADState({ operation: ADOPERATIONS.login }),
			});
		}
	};

	handleADResetPassword = () => {
		const { email } = this.state;
		const {
			msalContext: { instance: msalInstance },
		} = this.props;

		msalInstance.loginRedirect({
			loginHint: email,
			authority: ENV_OPTIONS.adb2c.policies.resetPassword,
			scopes: ENV_OPTIONS.adb2c.scopes,
			redirectUri: ROUTES.ADCallback,
			state: Utils.encodeADState({ operation: ADOPERATIONS.resetPassword }),
		});
	};

	render() {
		const { lang } = global;
		const {
			flags: { federation: federationFlag },
		} = this.props;
		const { validations, buttonState, email, isProcessingMsal } = this.state;
		if (isProcessingMsal) {
			return <SplashScreen />;
		}

		return (
			<div data-test-id="login-container" id="login-container">
				<div className="container">
					<div className="row">
						<div
							id="img-wrapper"
							className="col-sm-6 col-sm-offset-3"
						>
							<img
								src={BlueTextLogo}
								alt="RollKall"
							/>
						</div>
					</div>

					<div className="row">
						<div className="col-sm-6 col-sm-offset-3">
							<div id="login-panel">
								<If condition={!federationFlag}>
									<div
										className={classNames("form-group", {
											"has-error": validations.email,
										})}
									>
										<label
											className="control-label"
											htmlFor="login-email"
										>
											{lang.t("Email")}
										</label>
										<span className="input-group input-group-lg">
											<input
												className="form-control"
												disabled={buttonState === "loading"}
												id="login-email"
												name="email"
												type="email"
												value={email}
												placeholder={lang.t("Email")}
												onChange={this.updateField}
												onKeyPress={this.checkForEnter}
												data-test-id="login-email-input"
											/>
										</span>
									</div>
								</If>
								<ProgressButton
									className="btn btn-block btn-success btn-rounded"
									state={buttonState}
									onClick={this.validateLogin}
									data-test-id="login-button"
								>
									{lang.t("Login")}
								</ProgressButton>
							</div>
						</div>
					</div>
					<If condition={!federationFlag}>
						<div className="row">
							<div className="col-sm-6 col-sm-offset-3">
								<div id="login-panel-footer">
									<button
										type="button"
										className="btn btn-link footer-info"
										id="adb2c-password-recovery"
										data-test-id="adb2c-password-recovery-button"
										onClick={this.handleADResetPassword}
									>
										Forgot Password?
									</button>
								</div>
							</div>
						</div>
					</If>
				</div>
			</div>
		);
	}
}

Login.propTypes = {
	onLogin: PropTypes.func,
	msalContext: PropTypes.object.isRequired,
	flags: PropTypes.object.isRequired,
};

Login.defaultProps = {
	onLogin: () => undefined,
};

export default withLDConsumer()(withMsal(Login));
